/**
 * Whether the conversation history rail stands open beside the conversation, remembered per browser.
 *
 * ── Closed is the answer to everything, including the first visit ────────────────────────────
 * The owner's decision, 10 October 2026: the rail is hidden until the trainer opens it with the
 * sidebar toggle, and the choice is remembered. A value this build does not recognise (written by
 * a later one, or edited by hand), a store whose methods throw and a browser with no store at all
 * land on the same closed rail as the ordinary first visit: a record from another build is read
 * defensively and never allowed to throw.
 *
 * Read synchronously, before the first render (`useState`'s initialiser), so a trainer who keeps
 * it open never sees the conversation laid out without it for a frame — the same shape as the
 * navigation panel's `navPanelPreference`.
 */

import type { PreferenceStore } from '~/shared/lib/preferenceStore';

/** `movebuddy.` prefixed, as every preference this app stores is. */
const KEY = 'movebuddy.buddyHistoryRail';

const OPEN = 'open';
const CLOSED = 'closed';

export function readHistoryRailOpen(store: PreferenceStore | null): boolean {
  if (store === null) return false;
  try {
    return store.getItem(KEY) === OPEN;
  } catch {
    // Reading storage can THROW rather than answer null; a layout preference never takes the page down.
    return false;
  }
}

export function writeHistoryRailOpen(store: PreferenceStore | null, open: boolean): void {
  if (store === null) return;
  try {
    store.setItem(KEY, open ? OPEN : CLOSED);
  } catch {
    // A full or refused storage loses the memory of the choice, not the choice: the rail already
    // stands as the trainer asked.
  }
}
